/**
 * geo-popup.js
 * Frank Body — geo-region selection popup
 *
 * Flow:
 *   1. On page load, check for cookie `fb_geo_region`.
 *   2. If absent, call api.frankbody.com/geo-detect.php to get region.
 *   3. Show popup with detected region pre-filled.
 *   4. "Shop here" button sets cookie + redirects to detected URL.
 *   5. Region buttons set cookie + redirect immediately.
 *   6. Closing the popup (X / overlay) sets cookie to current store (no redirect).
 *   7. Header localization button also opens the popup (see below).
 */

(function () {
  'use strict';

  const COOKIE_NAME    = 'fb_geo_region';
  const COOKIE_DAYS    = 90;
  const GEO_API        = 'https://api2.frankbody.com/geo-detect.php';

  const STORE_LABELS = {
    AU: { name: 'Australia',    sub: 'frankbody.com',    url: 'https://frankbody.com',    flag: 'au' },
    UK: { name: 'United Kingdom', sub: 'uk.frankbody.com', url: 'https://uk.frankbody.com', flag: 'gb' },
    EU: { name: 'Europe',       sub: 'eu.frankbody.com', url: 'https://eu.frankbody.com', flag: 'eu' },
    US: { name: 'United States', sub: 'us.frankbody.com', url: 'https://us.frankbody.com', flag: 'us' },
    CA: { name: 'Canada',       sub: 'ca.frankbody.com', url: 'https://ca.frankbody.com', flag: 'ca' },
  };

  // ── Cookie helpers ────────────────────────────────────────────────────────────

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    // Set on root domain so all subdomains share it.
    // On apex domain (frankbody.com) the replace would give ".com" — use last two parts instead.
    const parts  = window.location.hostname.split('.');
    const domain = '.' + (parts.length >= 2 ? parts.slice(-2).join('.') : parts[0]);  // .frankbody.com
    document.cookie = name + '=' + encodeURIComponent(value)
      + '; expires=' + expires
      + '; path=/'
      + '; domain=' + domain
      + '; SameSite=Lax';
  }

  // ── Popup helpers ─────────────────────────────────────────────────────────────

  const popup      = document.getElementById('fb-geo-popup');
  if (!popup) return;

  const flagEl     = document.getElementById('fb-geo-flag');
  const labelEl    = document.getElementById('fb-geo-label');
  const subLabelEl = document.getElementById('fb-geo-sublabel');
  const stayBtn    = document.getElementById('fb-geo-stay-btn');
  const currentStore = popup.dataset.currentStore || 'AU';

  let detectedRegion   = null;
  let detectedUrl      = null;
  let detectedFallback = false;

  function openPopup() {
    popup.setAttribute('aria-hidden', 'false');
    popup.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closePopup(saveRegion) {
    popup.setAttribute('aria-hidden', 'true');
    popup.classList.remove('is-open');
    document.body.style.overflow = '';
    if (saveRegion) {
      setCookie(COOKIE_NAME, saveRegion, COOKIE_DAYS);
    }
  }

  const eyebrowEl = popup.querySelector('.fb-geo-popup__eyebrow');
  const detectedEl = document.getElementById('fb-geo-detected');
  const dividerEl  = popup.querySelector('.fb-geo-popup__divider');

  // Preserve current page path (and query) when redirecting to the other store.
  function urlWithCurrentPath(baseUrl) {
    return baseUrl + window.location.pathname + window.location.search;
  }

  function renderDetected(region, isFallback) {
    const store = STORE_LABELS[region] || STORE_LABELS['EU'];
    detectedRegion   = region;
    detectedUrl      = urlWithCurrentPath(store.url);
    detectedFallback = !!isFallback;

    if (isFallback) {
      // No regional site for this country — hide detected block and stay button
      if (eyebrowEl)  eyebrowEl.textContent = "We don't have a regional site for your location";
      if (detectedEl) detectedEl.hidden = true;
      stayBtn.hidden = true;
      if (dividerEl)  dividerEl.querySelector('span').textContent = 'Choose your region';
      // Clear current highlights — none should be pre-selected
      popup.querySelectorAll('.fb-geo-popup__region-btn').forEach(function (btn) {
        btn.classList.remove('is-current');
      });
    } else {
      if (eyebrowEl)  eyebrowEl.textContent = "You're visiting from";
      if (detectedEl) detectedEl.hidden = false;
      stayBtn.hidden = false;
      if (dividerEl)  dividerEl.querySelector('span').textContent = 'or choose a region';

      flagEl.innerHTML = '<img src="https://cdn.shopify.com/static/images/flags/' + store.flag + '.svg"'
        + ' alt="' + store.name + '" width="36" height="27" loading="eager">';
      labelEl.textContent    = store.name;
      subLabelEl.textContent = store.sub;
      stayBtn.href        = urlWithCurrentPath(store.url);
      stayBtn.textContent = 'Shop ' + store.name;

      popup.querySelectorAll('.fb-geo-popup__region-btn').forEach(function (btn) {
        btn.classList.toggle('is-current', btn.dataset.store === region);
      });
    }
  }

  // ── Geo detection ─────────────────────────────────────────────────────────────

  function detectAndShow() {
    const controller = new AbortController();
    const timeout    = setTimeout(function () { controller.abort(); }, 5000);

    fetch(GEO_API, { signal: controller.signal, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (data) {
        clearTimeout(timeout);
        const region   = data.region || currentStore;
        const fallback = data.fallback === true;
        console.log('[geo-popup]', { currentStore: currentStore, detected: region, fallback: fallback, country: data.country, source: data.source });
        // Cache for manual re-open via header button
        detectedRegion   = region;
        detectedFallback = fallback;
        // Only show popup if detected region differs from the current store
        // (or it's a fallback country with no dedicated store)
        if (fallback || region !== currentStore) {
          openPopup();
          renderDetected(region, fallback);
        }
        // else: visitor is already on the right store — do nothing
      })
      .catch(function () {
        clearTimeout(timeout);
        // API unavailable — don't bother the user
      });
  }

  // ── Event delegation ──────────────────────────────────────────────────────────

  popup.addEventListener('click', function (e) {
    // Close triggers (overlay + X button)
    if (e.target.closest('[data-geo-close]')) {
      e.preventDefault();
      closePopup(detectedRegion || currentStore);
      return;
    }

    // "Shop here" confirm
    if (e.target.closest('[data-geo-confirm]')) {
      if (!detectedUrl || detectedUrl === '#') return; // still loading
      e.preventDefault();
      setCookie(COOKIE_NAME, detectedRegion || currentStore, COOKIE_DAYS);
      window.location.href = detectedUrl;
      return;
    }

    // Region selection pills
    const regionBtn = e.target.closest('.fb-geo-popup__region-btn');
    if (regionBtn) {
      e.preventDefault();
      const region = regionBtn.dataset.store;
      const href   = regionBtn.href;
      if (region) setCookie(COOKIE_NAME, region, COOKIE_DAYS);
      window.location.href = href;
    }
  });

  // Keyboard: Escape closes
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) {
      closePopup(detectedRegion || currentStore);
    }
  });

  // ── Header localization button → open popup ───────────────────────────────────
  // Replace the existing `data-open-drawer="country"` behaviour.

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('.wt-localization-trigger--local');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // If popup is already open, close it; otherwise open fresh
    if (popup.classList.contains('is-open')) {
      closePopup(detectedRegion || currentStore);
    } else {
      // Re-open: render currently detected region if available
      openPopup();
      if (detectedRegion) {
        renderDetected(detectedRegion, detectedFallback);
      } else {
        // Geo never ran (cookie was already set) — assume current store
        renderDetected(currentStore, false);
      }
    }
  }, true); // capture phase so we intercept before localization-drawer.js

  // ── Init ──────────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    const cookie = getCookie(COOKIE_NAME);
    if (!cookie) {
      detectAndShow();
    }
    // If cookie exists, do nothing — user already chose.
  });

})();
